import { Text } from '@televet/kibble-ui/build/components/Text';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import {
  NotificationCounter,
  NotificationCounterVariant,
} from '@televet/kibble-ui/build/components/NotificationCounter';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import React, { useCallback, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  RecapBaseFragment,
  RecapStatus,
  SortOrder,
  useFindManyRecapQuery,
  useRetryProcessAppointmentRecapMutation,
  useRecapChangedSubscription,
} from 'shared/types/graphql';
import { openAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import { useAppDispatch } from 'state/hooks';
import { Box, Button, Divider, Flex, IconButton, Progress } from '@televet/kibble-ui';

const ProcessTracker = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [isOpen, setIsOpen] = React.useState(false);
  const dispatch = useAppDispatch();

  const {
    data: recapData,
    loading: isRecapDataLoading,
    refetch: refetchRecaps,
  } = useFindManyRecapQuery({
    variables: {
      where: {
        clinicId: {
          equals: currentClinicId,
        },
        createdAt: {
          // today
          gte: new Date(new Date().setHours(0, 0, 0, 0)),
        },
        appointment: {
          isDeleted: { equals: false },
        },
      },
      orderBy: {
        createdAt: SortOrder.Desc,
      },
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  useRecapChangedSubscription({
    variables: {
      where: {
        node: {
          clinicId: {
            equals: currentClinicId,
          },
        },
      },
    },
    skip: !currentClinicId,
    onData: (data) => {
      console.log('Recap changed subscription data:', data);
      refetchRecaps();
      setRetryingRecapIds([]);
    },
  });

  const activeProcesses = useMemo(() => {
    if (isRecapDataLoading) {
      return 0;
    }
    return (
      recapData?.findManyRecap.filter((recap) => !recap.generatedText && recap.status !== RecapStatus.Error).length || 0
    );
  }, [recapData, isRecapDataLoading]);

  const getProgressValue = useCallback((recap: RecapBaseFragment): { percentage: number; message: string } => {
    const messages = [
      'Turning barks and meows into text…',
      'Listening closely… even the tail wags.',
      'Translating from Woof to Words…',
      'Fetching the words from the audio bowl…',
      'Sniffing through the details…',
      'Analyzing like a bloodhound on a scent…',
      'Paw-sitively processing the facts…',
      'AI is thinking… and occasionally chasing its tail.',
      "Every bark tells a story. We're typing it out.",
      'Decoding woofs, meows, and awkward silences…',
      'Our AI just perked up its virtual ears.',
      'AI is putting on its thinking cone…',
      'Analyzing harder than a cat plotting world domination.',
      'Chewing on the data like a puppy with a new slipper.',
      'Turning tail wags into text… please hold.',
      'Did someone say squirrel? Re-focusing…',
      'Parsing ‘awwwws’ and medical jargon alike.',
      'Processing faster than a dog chasing a tennis ball',
    ];
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    if (recap.generatedText) return { percentage: 100, message: randomMessage };
    if (recap.transcriptionText) return { percentage: 75, message: randomMessage };
    if (recap.transcriptionS3Uri) return { percentage: 50, message: randomMessage };
    if (recap.recordingS3Uri) return { percentage: 25, message: randomMessage };
    return { percentage: 0, message: randomMessage };
  }, []);

  const [retryProcessRecap] = useRetryProcessAppointmentRecapMutation();

  const [retryingRecapIds, setRetryingRecapIds] = React.useState<string[]>([]);

  const retryRecap = useCallback(
    (recapId: string) => {
      setRetryingRecapIds((prev) => [...prev, recapId]);
      retryProcessRecap({
        variables: {
          data: {
            recapId,
          },
        },
      });

      console.log('Retrying recap processing for id:', recapId);
    },
    [retryProcessRecap],
  );

  const handleRecapClick = useCallback(
    (appointmentId: string | undefined) => {
      if (!appointmentId) {
        return;
      }
      dispatch(
        openAppointmentModal({
          appointmentId,
        }),
      );
    },
    [dispatch],
  );

  return (
    <Box zIndex={1000}>
      <Popover
        anchor={
          <IconButton
            marginLeft={4}
            size="sm"
            isActive={isOpen}
            aria-label="Download Recording"
            variant="secondarySubtle"
            icon={
              activeProcesses === 0 ? (
                <Icon size="sm" name="checkmarkCircle" />
              ) : (
                <>
                  <Flex position="absolute" top={-2} left={-2}>
                    <NotificationCounter count={activeProcesses} variant={NotificationCounterVariant.Blue} />
                  </Flex>
                  <Spinner size="sm" spinnerColor="background.secondary" />
                </>
              )
            }
          />
        }
        defaultIsOpen={false}
        headerProps={{
          children: (
            <>
              <Text as="p" mb={2} mt={2}>
                AI Recap Uploading & Processing
              </Text>
              <Text as="p" fontSize="md" fontWeight="normal" variant="subtle">
                {activeProcesses} active process{activeProcesses > 1 || activeProcesses === 0 ? 'es' : ''}
              </Text>
              <Divider mt={4} />
            </>
          ),
          paddingX: 5,
          paddingBottom: 0,
        }}
        placement="bottom-end"
        size="xl"
        trigger="click"
        variant="default"
        onOpen={(): void => {
          setIsOpen(true);
        }}
        onClose={(): void => {
          setIsOpen(false);
        }}
      >
        <Box paddingTop={0} maxHeight="80vh" overflowY="scroll">
          {recapData?.findManyRecap.map((recap) => {
            const completion = getProgressValue(recap);
            const isProcessComplete =
              completion.percentage === 100 ||
              recap.status === RecapStatus.Complete ||
              recap.status === RecapStatus.Error;
            return (
              <Box key={recap.id} onClick={(): void => handleRecapClick(recap.appointment?.id)} cursor="pointer">
                <Box _hover={{ bg: 'background.alpha' }} cursor="pointer" padding={5}>
                  <Flex justifyContent="space-between" alignItems="center" gap="4" mb={2}>
                    <Text as="p" fontWeight="bold">
                      {recap.appointment?.clinicPet?.name}&apos;s AI Recap
                    </Text>
                    <Text as="p" variant="subtle">
                      {formatDistanceToNow(new Date(recap.createdAt), { addSuffix: true })}
                    </Text>
                  </Flex>
                  <Flex mb={0} alignItems="center" gap={1}>
                    {isProcessComplete && (
                      <>
                        {recap.status !== RecapStatus.Error ? (
                          <>
                            {' '}
                            <Icon name={'checkmark'} size="sm" />
                            <Text as="p" fontSize="sm">
                              Notes by{' '}
                              {recap.createdBy.nameDisplay ||
                                `${recap.createdBy?.firstName} ${recap.createdBy?.lastName}`}
                            </Text>
                          </>
                        ) : (
                          <Box>
                            <Flex alignItems="center" gap={1}>
                              <Icon name="warningSign" color="text.warning" size="md" />
                              <Text as="p" fontSize="sm">
                                Processing failed
                              </Text>
                            </Flex>
                            <Button
                              size="sm"
                              variant="secondary"
                              iconName="arrowCircle"
                              mt={3}
                              onClick={(e): void => {
                                e.stopPropagation();
                                e.preventDefault();
                                retryRecap(recap.id);
                              }}
                              isLoading={retryingRecapIds.includes(recap.id)}
                            >
                              Retry
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                    <Icon name="externalLink" size="sm" ml="auto" variant="subtle" />
                  </Flex>

                  {!isProcessComplete && (
                    <>
                      <Progress
                        mb={1}
                        mt={2}
                        value={completion.percentage}
                        size="md"
                        color="background/secondary"
                        hasStripe
                        isAnimated
                        rounded="full"
                      />

                      <Text fontStyle="italic" as="p" fontSize="xs" color="text.subtle">
                        {completion.message}
                      </Text>
                    </>
                  )}
                </Box>
                <Divider />
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
};

export default ProcessTracker;
